import type { StaleConnection, StaleConnectionReason } from './types';
import { STALENESS_DISMISS_STORAGE_KEY } from './constants';
import { staleConnectionsSignature } from './utils';

export type StalenessDismissals = Partial<Record<StaleConnectionReason, string>>;

function isReason(value: string): value is StaleConnectionReason {
  return value === 'disconnected' || value === 'delayed-feed';
}

export function parseDismissals(raw: string | null): StalenessDismissals {
  if (raw === null) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }
    return Object.entries(parsed).reduce<StalenessDismissals>((dismissals, [reason, signature]) => {
      if (isReason(reason) && typeof signature === 'string') {
        return { ...dismissals, [reason]: signature };
      }
      return dismissals;
    }, {});
  } catch {
    return {};
  }
}

export function readDismissals(): StalenessDismissals {
  if (typeof window === 'undefined') {
    return {};
  }
  return parseDismissals(window.sessionStorage.getItem(STALENESS_DISMISS_STORAGE_KEY));
}

export function writeDismissals(dismissals: StalenessDismissals) {
  if (typeof window === 'undefined') {
    return;
  }
  window.sessionStorage.setItem(STALENESS_DISMISS_STORAGE_KEY, JSON.stringify(dismissals));
}

export function undismissedConnections(
  connections: readonly StaleConnection[],
  dismissals: StalenessDismissals,
): readonly StaleConnection[] {
  return connections.filter((connection) => {
    const sameReason = connections.filter((candidate) => candidate.reason === connection.reason);
    return dismissals[connection.reason] !== staleConnectionsSignature(sameReason);
  });
}

export function dismissalsWith(
  dismissals: StalenessDismissals,
  connections: readonly StaleConnection[],
  reason: StaleConnectionReason,
): StalenessDismissals {
  const sameReason = connections.filter((connection) => connection.reason === reason);
  return { ...dismissals, [reason]: staleConnectionsSignature(sameReason) };
}
